/**
 * Nova Poshta Integration Logic
 * Handles City and Warehouse selection using official API
 */

const NP_CONFIG = {
  apiKey: '2784b4333fcd9c92d67a802c1ad77045',
  apiUrl: 'https://api.novaposhta.ua/v2.0/json/'
};

class NovaPoshtaSelector {
  constructor(container) {
    this.container = container;
    this.language = container.dataset.language === 'RU' || container.dataset.language === 'ru' ? 'ru' : 'UA';
    this.showPostomats = container.dataset.showPostomats === 'true';
    
    // Localized Messages
    this.messages = {
      processing: container.dataset.msgProcessing || 'Processing...',
      loadingBranches: container.dataset.msgLoadingBranches || 'Loading branches...',
      branchPlaceholder: container.dataset.placeholderBranch || 'Select a branch',
      courierSuffix: container.dataset.msgCourierSuffix || 'Courier delivery',
    };
    
    // Common Elements
    this.cityInput = container.querySelector('#np-city');
    this.hiddenCity = container.querySelector('#np-city-name');
    this.hiddenCityRef = container.querySelector('#np-city-ref');
    this.cityDropdown = container.querySelector('#np-city-dropdown');
    
    // Warehouse Mode
    this.warehouseGroup = container.querySelector('#np-warehouse-group');
    this.branchInput = container.querySelector('#np-branch');
    this.hiddenBranch = container.querySelector('#np-branch-name');
    this.branchDropdown = container.querySelector('#np-branch-dropdown');
    
    // Address Mode
    this.addressGroup = container.querySelector('#np-address-group');
    this.streetInput = container.querySelector('#np-street');
    this.hiddenStreet = container.querySelector('#np-street-name');
    this.streetDropdown = container.querySelector('#np-street-dropdown');
    this.houseInput = container.querySelector('#np-house');
    this.flatInput = container.querySelector('#np-flat');

    // Mode Switcher
    this.modeRadios = container.querySelectorAll('input[name="np_delivery_type"]');

    // State
    this.currentCityRef = null;
    this.currentSettlementRef = null;
    this.currentBranches = [];
    this.deliveryMode = 'warehouse';

    this.init();
  }

  init() {
    this.setupEventListeners();
    // Note: Checkout button is handled by inline onclick in HTML
    this.setMode('warehouse');
  }


  setupCheckoutButton() {
    // Find the checkout button and attach our handler
    const checkoutBtn = document.querySelector('#CartDrawer-Checkout');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', (e) => this.onCheckoutClick(e, checkoutBtn));
    }
  }

  onCheckoutClick(e, btn) {
    e.preventDefault();
    e.stopPropagation();

    // Step 1: Show NP container if hidden
    if (!this.container.classList.contains('np-visible')) {
      this.container.classList.add('np-visible');
      setTimeout(() => {
        this.container.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 100);
      return; // Stop here, wait for user to fill fields
    }

    // Step 2: Validate fields
    const validation = this.validateFields();
    
    if (!validation.valid) {
      // Fields are invalid - do NOT proceed
      return;
    }

    // Step 3: All valid - redirect to checkout
    btn.classList.add('loading');
    btn.innerHTML = this.messages.processing;
    
    this.redirectToCheckout(validation.addressLine);
  }

  validateFields() {
    let valid = true;
    let addressLine = '';

    // Reset error styles
    this.container.querySelectorAll('.np-input').forEach(input => {
      input.style.borderColor = '';
    });

    const markError = (input) => {
      valid = false;
      if (input) {
        input.style.borderColor = '#ff4444';
        // Clear error after 5 seconds
        setTimeout(() => {
          input.style.borderColor = '';
        }, 5000);
      }
    };


    // Check city
    if (!this.hiddenCity.value) {
      markError(this.cityInput);
      return { valid: false, addressLine: '' };
    }

    // Check based on mode
    if (this.deliveryMode === 'warehouse') {
      if (!this.hiddenBranch.value) {
        markError(this.branchInput);
        return { valid: false, addressLine: '' };
      }
      addressLine = this.hiddenBranch.value;
    } else {
      // Address mode
      if (!this.streetInput.value) {
        markError(this.streetInput);
        return { valid: false, addressLine: '' };
      }
      if (!this.houseInput.value) {
        markError(this.houseInput);
        return { valid: false, addressLine: '' };
      }
      
      addressLine = `${this.streetInput.value}, буд.${this.houseInput.value}`;
      if (this.flatInput.value) {
        addressLine += `, кв.${this.flatInput.value}`;
      }
      addressLine += ` (${this.messages.courierSuffix})`;
    }

    return { valid: true, addressLine };
  }

  async redirectToCheckout(addressLine) {
    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();

      if (cart.items.length === 0) {
        window.location.reload();
        return;
      }

      // Prepare cart-level attributes (Nova Poshta data)
      const attributes = {
        'Nova Poshta City': this.hiddenCity.value
      };

      if (this.deliveryMode === 'warehouse') {
        attributes['Nova Poshta Branch'] = this.hiddenBranch.value;
        attributes['Nova Poshta Mode'] = 'Warehouse';
      } else {
        attributes['Nova Poshta Address Full'] = addressLine;
        attributes['Nova Poshta Mode'] = 'Courier';
      }

      // Update cart attributes WITHOUT modifying line items (preserves properties like "Напис на стрічку")
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });

      // Redirect to checkout with shipping address pre-filled
      const params = new URLSearchParams();
      params.append('checkout[shipping_address][city]', this.hiddenCity.value);
      params.append('checkout[shipping_address][address1]', addressLine);
      params.append('checkout[shipping_address][country]', 'Ukraine');
      params.append('checkout[shipping_address][zip]', '12345');

      window.location.href = `/checkout?${params.toString()}`;
    } catch (err) {
      console.error('Checkout redirect error:', err);
      window.location.href = '/checkout';
    }
  }

  setupEventListeners() {
    // Mode Switcher
    this.modeRadios.forEach(radio => {
      radio.addEventListener('change', (e) => this.setMode(e.target.value));
    });

    // Quick City Selection
    this.container.querySelectorAll('.np-quick-city').forEach(qc => {
      qc.addEventListener('click', () => {
        const cityName = qc.dataset.city;
        this.cityInput.value = cityName;
        this.handleCitySearch(cityName, true);
      });
    });

    // City Input
    this.cityInput.addEventListener('input', this.debounce((e) => {
      this.handleCitySearch(e.target.value);
    }, 400));
    
    this.cityInput.addEventListener('focus', () => {
      if (this.cityInput.value.length >= 2 && !this.currentCityRef) {
        this.handleCitySearch(this.cityInput.value);
      } else if (this.cityDropdown.children.length > 0) {
        this.cityDropdown.classList.add('active');
      }
    });

    // Branch Input
    this.branchInput.addEventListener('input', () => this.filterBranches());
    this.branchInput.addEventListener('focus', () => {
      if (this.currentBranches.length > 0) {
        this.branchDropdown.classList.add('active');
        this.filterBranches();
      } else if (this.currentCityRef) {
        this.loadBranches(this.currentCityRef);
      }
    });

    // Street Input
    this.streetInput.addEventListener('input', this.debounce((e) => {
      this.handleStreetSearch(e.target.value);
    }, 400));

    this.streetInput.addEventListener('focus', () => {
      if (this.streetDropdown.children.length > 0) {
        this.streetDropdown.classList.add('active');
      }
    });

    // Click Outside to close dropdowns
    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target)) {
        this.closeDropdowns();
      }
    });
  }

  setMode(mode) {
    this.deliveryMode = mode;
    if (mode === 'warehouse') {
      this.warehouseGroup.style.display = 'block';
      this.addressGroup.style.display = 'none';
    } else {
      this.warehouseGroup.style.display = 'none';
      this.addressGroup.style.display = 'flex';
      if (this.currentCityRef) {
        this.streetInput.disabled = false;
      }
    }
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  async fetchNP(modelName, calledMethod, methodProperties) {
    try {
      const response = await fetch(NP_CONFIG.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiKey: NP_CONFIG.apiKey,
          modelName,
          calledMethod,
          methodProperties
        })
      });
      const data = await response.json();
      return data.success ? data.data : [];
    } catch (e) {
      console.error('Nova Poshta API Error:', e);
      return [];
    }
  }

  async handleCitySearch(query, autoSelect = false) {
    if (!query || query.length < 2) {
      this.closeDropdowns();
      return;
    }

    const cities = await this.fetchNP('Address', 'searchSettlements', {
      CityName: query,
      Limit: autoSelect ? 1 : 20,
      Language: this.language
    });

    const formattedCities = cities[0]?.Addresses || [];
    
    if (autoSelect && formattedCities.length > 0) {
      this.selectCity(formattedCities[0]);
      return;
    }

    this.renderDropdown(this.cityDropdown, formattedCities, (item) => {
      const main = item.MainDescription;
      const area = item.AreaDescription ? `(${item.AreaDescription})` : '';
      return `${main} ${area}`;
    }, (item) => {
      this.selectCity(item);
    });
  }

  selectCity(cityItem) {
    this.cityInput.value = cityItem.MainDescription;
    this.hiddenCity.value = cityItem.MainDescription;
    this.currentCityRef = cityItem.DeliveryCity;
    this.currentSettlementRef = cityItem.Ref;
    if (this.hiddenCityRef) this.hiddenCityRef.value = cityItem.DeliveryCity;
    
    // Reset Branch
    this.branchInput.value = '';
    this.hiddenBranch.value = '';
    this.branchInput.disabled = false;
    this.branchInput.placeholder = this.messages.loadingBranches;

    // Reset Street
    this.streetInput.value = '';
    this.hiddenStreet.value = '';
    this.streetInput.disabled = false;
    
    this.closeDropdowns();
    this.loadBranches(this.currentCityRef);
  }

  async loadBranches(cityRef) {
    const branches = await this.fetchNP('AddressGeneral', 'getWarehouses', {
      CityRef: cityRef,
      Language: this.language,
      Limit: 500
    });
    
    this.currentBranches = this.filterPostomats(branches);
    this.branchInput.placeholder = this.messages.branchPlaceholder;
  }

  filterBranches() {
    const query = this.branchInput.value.toLowerCase();
    const filtered = this.currentBranches.filter(b => 
      b.Description.toLowerCase().includes(query) || b.Number.includes(query)
    );
    
    this.renderDropdown(this.branchDropdown, filtered.slice(0, 50), 
      (item) => item.Description,
      (item) => this.selectBranch(item)
    );
  }

  filterPostomats(branches) {
    return branches.filter((branch) => {
      if (this.isPickupPoint(branch)) return false;
      if (this.isMobileWarehouse(branch)) return false;
      if (this.showPostomats) return true;

      return !this.isPostomat(branch);
    });
  }

  isPostomat(branch) {
    const haystack = this.getBranchSearchText(branch);

    return haystack.includes('поштомат') || haystack.includes('почтомат') || haystack.includes('postomat');
  }

  isPickupPoint(branch) {
    return this.getBranchSearchText(branch).includes('пункт');
  }

  isMobileWarehouse(branch) {
    return this.getBranchSearchText(branch).includes('мобільне відділення');
  }

  getBranchSearchText(branch) {
    return [
      branch.Description,
      branch.ShortAddress,
      branch.DescriptionRu,
      branch.ShortAddressRu,
      branch.TypeOfWarehouse,
      branch.CategoryOfWarehouse
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  selectBranch(branchItem) {
    this.branchInput.value = branchItem.Description;
    this.hiddenBranch.value = branchItem.Description;
    this.closeDropdowns();
  }

  async handleStreetSearch(query) {
    if (!this.currentSettlementRef || query.length < 2) return;

    const streets = await this.fetchNP('Address', 'searchSettlementStreets', {
      SettlementRef: this.currentSettlementRef,
      StreetName: query,
      Language: this.language,
      Limit: 20
    });

    const addresses = streets[0]?.Addresses || [];
    this.renderDropdown(this.streetDropdown, addresses, 
      (item) => item.Present, 
      (item) => {
        this.streetInput.value = item.Present;
        this.hiddenStreet.value = item.Present;
        this.closeDropdowns();
      }
    );
  }

  renderDropdown(element, items, labelFn, onClick) {
    element.innerHTML = '';
    if (items.length === 0) {
      element.classList.remove('active');
      return;
    }

    items.forEach(item => {
      const div = document.createElement('div');
      div.className = 'np-option';
      div.textContent = labelFn(item);
      div.addEventListener('click', () => onClick(item));
      element.appendChild(div);
    });

    element.classList.add('active');
  }

  closeDropdowns() {
    this.cityDropdown.classList.remove('active');
    this.branchDropdown.classList.remove('active');
    this.streetDropdown.classList.remove('active');
  }
}


// Global initialization
window.initNovaPoshta = () => {
  const container = document.querySelector('#nova-poshta-container-drawer');
  if (!container) {
    console.log('NP: Container not found');
    return;
  }
  
  // Check if already initialized on this specific container
  if (container.dataset.npInitialized === 'true') {
    console.log('NP: Already initialized');
    return;
  }
  
  console.log('NP: Initializing...');
  container.dataset.npInitialized = 'true';
  window.NovaPoshtaSelectorInstance = new NovaPoshtaSelector(container);
};

// Initialize on load
if (document.readyState !== 'loading') {
  window.initNovaPoshta();
} else {
  document.addEventListener('DOMContentLoaded', window.initNovaPoshta);
}

// Re-init on cart updates (for dynamic drawer)
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    // Give DOM time to update
    setTimeout(() => {
      const container = document.querySelector('#nova-poshta-container-drawer');
      if (container) {
        container.dataset.npInitialized = 'false';
        window.NovaPoshtaSelectorInstance = null;
        window.initNovaPoshta();
      }
    }, 100);
  });
}
